import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';

import {
  formatCurrency,
  formatDateTime,
  getCollection,
  normalizeInvoice,
  toTitleCase
} from './helpers';

export default function CustomProductInvoices() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    return `/api/admin/custom-product-invoices?${params.toString()}`;
  }, [page, rowsPerPage, search]);

  const { data, error, loading } = useGet(endpoint);
  const invoices = getCollection(data, ['invoices']).map(normalizeInvoice);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h4">Custom Product Invoices</Typography>
        <Typography color="text.secondary">
          Browse admin invoices generated for custom product payments.
        </Typography>
      </Stack>

      <MainCard>
        <TextField
          fullWidth
          size="small"
          label="Search by invoice, order, customer, or product"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      </MainCard>

      {error ? <Alert severity="error">Failed to load custom product invoices.</Alert> : null}

      <MainCard title={`Invoices (${Number(data?.total || invoices.length)})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading custom product invoices...
                  </TableCell>
                </TableRow>
              ) : invoices.length ? (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.invoiceNumber || invoice.id}</TableCell>
                    <TableCell>{invoice.order?.id || '-'}</TableCell>
                    <TableCell>{invoice.customProduct?.title || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={toTitleCase(invoice.status)} />
                    </TableCell>
                    <TableCell>{invoice.currency}</TableCell>
                    <TableCell>{formatCurrency(invoice.currency, invoice.totalAmount)}</TableCell>
                    <TableCell>{formatDateTime(invoice.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/custom-products/invoices/${invoice.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No custom product invoices found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(data?.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>
    </Stack>
  );
}
